// ============================================================
// SurvivalCurveChart - a survival curve (attacks vs survival %).
// ============================================================
// Custom SVG line chart (not using BarChart), shared by the attack side's
// wounds-to-kill and the defence side's survival: an area under the
// curve, the 0/50/100% grid, the average-attacks marker, and the axes.
// The two differ only in colour.

interface SurvivalData {
  /** Survival probability after N attacks; [0] = 1.0. */
  survivalCurve: number[];
  meanAttacks: number;
}

const W = 300;
const H = 150;
const PAD = { top: 15, right: 10, bottom: 24, left: 32 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

const Y_LABELS = [
  { val: 1.0, label: '100%' },
  { val: 0.5, label: '50%' },
  { val: 0.0, label: '0%' },
];

/** The x of attack `i` of `n`, and the y of a survival share. */
const xAt = (i: number, n: number) => PAD.left + (i / (n - 1)) * PLOT_W;
const yAt = (s: number) => PAD.top + (1 - s) * PLOT_H;

/** Roughly six x labels, always including the last attack. */
export function xAxisTicks(n: number): number[] {
  const step = Math.max(1, Math.floor((n - 1) / 6));
  const ticks: number[] = [];
  for (let i = 0; i < n; i += step) ticks.push(i);
  if (ticks[ticks.length - 1] !== n - 1) ticks.push(n - 1);
  return ticks;
}

export default function SurvivalCurveChart({ data, colour }: { data: SurvivalData; colour: string }) {
  const n = data.survivalCurve.length;
  if (n < 2) return null;
  const points = data.survivalCurve.map((s, i) => `${xAt(i, n)},${yAt(s)}`);
  const areaPoints = [`${PAD.left},${PAD.top + PLOT_H}`, ...points, `${PAD.left + PLOT_W},${PAD.top + PLOT_H}`];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%' }}>
      <polygon points={areaPoints.join(' ')} fill={colour} opacity={0.15} />
      {Y_LABELS.map(({ val }) => (
        <line
          key={val}
          x1={PAD.left}
          y1={yAt(val)}
          x2={PAD.left + PLOT_W}
          y2={yAt(val)}
          stroke="#444"
          strokeWidth={0.5}
        />
      ))}
      <polyline points={points.join(' ')} fill="none" stroke={colour} strokeWidth={1.5} />
      {data.meanAttacks < n && <MeanMarker x={xAt(data.meanAttacks, n)} mean={data.meanAttacks} />}
      <Axes n={n} />
    </svg>
  );
}

/** The dashed average-attacks line and its label. */
function MeanMarker({ x, mean }: { x: number; mean: number }) {
  return (
    <g>
      <line x1={x} y1={PAD.top} x2={x} y2={PAD.top + PLOT_H} stroke="#f59e0b" strokeWidth={0.8} strokeDasharray="3,2" />
      <text x={x} y={PAD.top - 3} textAnchor="middle" fontSize={7} fill="#f59e0b">
        avg {mean.toFixed(1)}
      </text>
    </g>
  );
}

/** The y labels, the attack-count x labels, and the two axis lines. */
function Axes({ n }: { n: number }) {
  return (
    <>
      {Y_LABELS.map(({ val, label }) => (
        <text key={val} x={PAD.left - 3} y={yAt(val) + 3} textAnchor="end" fontSize={7} fill="#777">
          {label}
        </text>
      ))}
      {xAxisTicks(n).map((idx) => (
        <text key={idx} x={xAt(idx, n)} y={PAD.top + PLOT_H + 14} textAnchor="middle" fontSize={7} fill="#777">
          {String(idx)}
        </text>
      ))}
      <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + PLOT_H} stroke="#555" strokeWidth={0.5} />
      <line
        x1={PAD.left}
        y1={PAD.top + PLOT_H}
        x2={PAD.left + PLOT_W}
        y2={PAD.top + PLOT_H}
        stroke="#555"
        strokeWidth={0.5}
      />
    </>
  );
}
